"use client";

import CallToAction from "@/components/CallToAction";
import FAQs from "@/components/FAQs";
import HighlightTitle from "@/components/HighlightTitle";
import { Image } from "@/components/Image";
import Page from "@/components/Page";
import Checklist from "@/components/animations/Checklist";
import CompletionRing from "@/components/animations/CompletionRing";
import EquipmentStatus from "@/components/animations/EquipmentStatus";
import Section from "@/components/ui/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { buttonKey, textKey } from "@/lib/list-keys";
import { tinaField, useTina } from "@/lib/utils";
import { BarChart, ClipboardCheck, Clock, FileText, History, QrCode, Shield, Smartphone, Users, Zap } from "lucide-react";
import Link from "next/link";
const iconMap = {
  FileText,
  Shield,
  Clock,
  Smartphone,
  QrCode,
  ClipboardCheck,
  BarChart,
  History,
  Users,
  Zap,
};

export default function PageClient(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, usps, features, faqs, cta } = data.home;

  return (
    <Page defaultTinaField={data.home}>
      <Header tinaFieldData={data.home.header} {...header} />
      <USPs tinaFieldData={data.home.usps} items={usps?.items} />
      <Features tinaFieldData={data.home.features} {...features} />
      <FAQs tinaFieldData={data.home.faqs} {...faqs} />
      <CallToAction tinaFieldData={data.home.cta} {...cta} />
    </Page>
  );
}

function Header({ tinaFieldData, pretitle, title, subtitle, ctas, image }) {
  return (
    <Section as="header" wrapperClassName="bg-muted">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] xl:grid-cols-[1fr_540px] gap-8 md:gap-12 lg:gap-16">
        <div className="flex flex-col gap-4 order-2 lg:order-1 lg:mt-12">
          {pretitle && (
            <Badge data-tina-field={tinaField(tinaFieldData, "pretitle")} className="w-fit bg-white">
              {pretitle}
            </Badge>
          )}
          <HighlightTitle as="h1" className="h0 text-balance" content={title} data-tina-field={tinaField(tinaFieldData, "title")} />
          <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="p-lg max-w-[540px]">
            {subtitle}
          </p>
          {ctas?.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-4 mt-4 lg:mt-12">
              {ctas.map((btn, index) => (
                <Button
                  key={buttonKey(btn, index)}
                  data-tina-field={tinaField(ctas[index], "label")}
                  asChild
                  size="lg"
                  variant={index === 0 ? "default" : "outline"}
                  className="w-full sm:w-auto"
                  arrow={index === 0}
                >
                  <Link href={btn.link || "/"}>{btn.label}</Link>
                </Button>
              ))}
            </div>
          )}
        </div>
        <div className="order-1 lg:order-2 relative">
          {/* Floating widgets — compact (ring) and standard (checklist, status) */}
          <div className="absolute z-20 w-22 sm:w-36 lg:w-36 2xl:w-44 top-8 lg:top-16 -right-4 sm:-right-8 2xl:-right-24">
            <CompletionRing value={80} initialDelay={0} />
          </div>
          <div className="absolute z-10 w-32 sm:w-48 lg:w-52 2xl:w-56 bottom-6 lg:bottom-28 -left-4 sm:-left-6 xl:-left-20">
            <Checklist initialDelay={0.1} />
          </div>
          <div className="absolute z-10 w-28 sm:w-48 lg:w-52 2xl:w-56 -bottom-4 sm:-bottom-12 lg:-bottom-12 left-3/5 lg:left-1/2 hidden sm:block">
            <EquipmentStatus initialDelay={0.2} />
          </div>
          <Image
            src={image}
            alt={title || ""}
            wrapperClassName="aspect-[16/11] md:aspect-[1/1.1] max-lg:max-h-[400px] w-full"
            className="object-cover rounded-3xl object-top"
            data-tina-field={tinaField(tinaFieldData, "image")}
            sizes="(min-width: 1024px) 620px, 100vw"
            priority
          />
        </div>
      </div>
    </Section>
  );
}

function USPs({ tinaFieldData, items }) {
  if (!items?.length) return null;
  return (
    <Section wrapperClassName="py-8 md:py-10 lg:py-16 !pb-4" className="border-b border-border pb-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
        {items.map((usp, index) => {
          const Icon = iconMap[usp.icon];
          return (
            <div
              key={`${usp.icon ?? "usp"}-${textKey(usp.label, index)}`}
              className="flex flex-col items-center gap-4 text-center"
              data-tina-field={tinaField(tinaFieldData?.items?.[index], "label")}
            >
              {Icon && <Icon className="size-14 text-primary bg-muted p-3.5 rounded-lg" aria-hidden="true" />}
              <span className="text-sm md:text-base font-light text-balance max-w-[200px]">{usp.label}</span>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Features({ tinaFieldData, pretitle, title, subtitle, button: btn, features }) {
  return (
    <Section>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_500px] gap-4 md:gap-6 lg:gap-12 mb-8 lg:mb-20">
        <div>
          {pretitle && (
            <Badge data-tina-field={tinaField(tinaFieldData, "pretitle")} className="mb-4">
              {pretitle}
            </Badge>
          )}
          <h2 data-tina-field={tinaField(tinaFieldData, "title")} className="h1 text-balance">
            {title}
          </h2>
        </div>
        <div className="flex flex-col justify-end gap-4 items-start">
          {subtitle && (
            <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="text-subtitle text-balance">
              {subtitle}
            </p>
          )}
          {btn?.label && (
            <Button data-tina-field={tinaField(tinaFieldData?.button, "label")} asChild variant="link" arrow={true}>
              <Link href={btn.link || "/"}>
                {btn.label} <span className="sr-only">about product</span>
              </Link>
            </Button>
          )}
        </div>
      </div>
      {features?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card key={textKey(feature.title, index)} className="flex flex-col gap-2 p-6 md:p-8">
              {feature.image && (
                <Image
                  data-tina-field={tinaField(features[index], "image")}
                  src={feature.image}
                  wrapperClassName="w-[264px] aspect-[1/1] overflow-hidden -mt-16 self-center"
                  className="object-contain object-bottom w-full h-full translate-y-12"
                  alt={feature.title || `Feature image for ${feature.title}`}
                  sizes="(min-width: 1024px) 264px, 100vw"
                  priority
                  fill
                >
                  <div className="absolute bottom-0 left-0 w-full h-16 bg-linear-to-t from-gray-75 to-transparent" />
                </Image>
              )}
              <h3 data-tina-field={tinaField(features[index], "title")} className="text-lg text-title mt-4">
                {feature.title}
              </h3>
              {feature.text && (
                <p data-tina-field={tinaField(features[index], "text")} className="">
                  {feature.text}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}
